/**
 * ThemeProvider + useTheme — the React surface for the device-local appearance
 * choice (T12.2 step 10a).
 *
 * Holds the current `ThemeChoice` (System / Light / Dark), persists it, and runs
 * the side effect that sets `<html data-theme="dark">` so the dark token block
 * (`[data-theme="dark"]` injected in main.tsx) cascades app-wide. A `system`
 * choice live-updates when the OS theme flips while the app is open.
 *
 * The App-settings Theme control reads `choice` + calls `setChoice`. Any other
 * surface can read the resolved palette via `resolved`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  applyResolvedTheme,
  persistThemeChoice,
  prefersDark,
  readPersistedThemeChoice,
  resolveTheme,
  subscribeSystemTheme,
  type ResolvedTheme,
  type ThemeChoice,
} from './theme';
import { applyNativeNavBarAppearance } from '../adapters/systemUi';

export interface ThemeContextValue {
  /** The user's chosen mode (System / Light / Dark). */
  choice: ThemeChoice;
  /** The effective palette after resolving System against the OS. */
  resolved: ResolvedTheme;
  /** Set + persist the choice; immediately re-applies `data-theme`. */
  setChoice: (choice: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => readPersistedThemeChoice());
  // Track the OS preference so a `system` choice can re-resolve when it flips.
  const [systemDark, setSystemDark] = useState<boolean>(() => prefersDark());

  // Resolve + apply on every change to either the choice or the OS preference.
  const resolved = resolveTheme(choice, systemDark);
  useEffect(() => {
    applyResolvedTheme(resolved);
    // On the native Android shell, make the OS system-nav-bar icons follow the
    // app theme (dark icons in light mode, light icons in dark) so the ◀ ● ■
    // buttons always contrast with the app's bottom chrome. A NO-OP on web/
    // desktop/iOS and fail-soft, so the theme effect is never broken by it.
    void applyNativeNavBarAppearance(resolved);
  }, [resolved]);

  // Live-update the OS preference while the app is open (only matters for the
  // `system` choice, but tracking it unconditionally keeps the effect simple).
  useEffect(() => subscribeSystemTheme(setSystemDark), []);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    persistThemeChoice(next);
  }, []);

  return (
    <ThemeContext.Provider value={{ choice, resolved, setChoice }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error(
      'useTheme must be used inside <ThemeProvider>. main.tsx wraps the tree.',
    );
  }
  return ctx;
}
